import { CustomSettings } from "subModule/src/services/invoice/types";
import {
  ThermalDeliveryNote,
  ThermalInvoiceData,
  ThermalInvoiceLineItem,
} from "./ThermalInvoiceContent";

function formatItemDescription(item: any, settings: CustomSettings): string {
  const opts = settings.itemDisplayOptions;
  const title = item.title || item.itemName || item.packageName || "";
  const code =
    item.code || item.itemCode || item.serviceCode || item.packageCode || "";

  if (opts?.showCode && !opts?.showTitle) return code || title;
  if (opts?.showTitle && opts?.showCode && code) return `${title} (${code})`;
  return title || code;
}

function collectLineItems(invoice: any, settings: CustomSettings): ThermalInvoiceLineItem[] {
  const result: ThermalInvoiceLineItem[] = [];

  const pushItem = (raw: any, type: string) => {
    result.push({
      type,
      title: raw.title || raw.itemName || raw.packageName || "",
      code:
        raw.code ||
        raw.itemCode ||
        raw.serviceCode ||
        raw.packageCode ||
        "",
      description: raw.description || formatItemDescription(raw, settings),
      quantity: Number(raw.quantity) || 1,
      unitPrice: Number(raw.unitPrice ?? raw.price) || 0,
      discount: Number(raw.discount) || 0,
      tax: Number(raw.taxAmount ?? raw.tax) || 0,
      amount: Number(raw.totalAmount ?? raw.amount) || 0,
    });
  };

  (invoice.invoicePackages || []).forEach((pkg: any) => {
    pushItem(pkg, "package");
    (pkg.services || []).forEach((s: any) => {
      pushItem(s, "service");
      (s.items || []).forEach((i: any) => pushItem(i, "item"));
    });
    (pkg.items || []).forEach((i: any) => pushItem(i, "item"));
  });

  (invoice.invoiceServices || []).forEach((s: any) => {
    pushItem(s, "service");
    (s.items || []).forEach((i: any) => pushItem(i, "item"));
  });

  (invoice.invoiceItems || []).forEach((i: any) => pushItem(i, "item"));

  return result;
}

function collectDeliveryNotes(invoice: any): ThermalDeliveryNote[] {
  const notes =
    invoice.deliveryNotes ||
    invoice.consolidatedDeliveryNotes ||
    invoice.invoiceDeliveryNotes ||
    [];

  return (notes as any[]).map((note: any) => ({
    vehicleName: note.vehicleName || note.vehicle?.name || "",
    vehiclePlateNo: note.vehiclePlateNo || note.plateNumber || "",
    deliveryNoteNo: note.deliveryNoteNo || note.deliveryNoteNumber || "",
    subtotal: Number(note.subtotalAmount ?? note.subTotal) || 0,
    discount: Number(note.discount) || 0,
    tax: Number(note.taxAmount ?? note.tax) || 0,
    total: Number(note.totalAmount ?? note.total) || 0,
  }));
}

export function mapInvoiceToThermalData(
  invoice: any,
  settings: CustomSettings,
): ThermalInvoiceData {
  const attrs = invoice?.attributes || {};

  const isConsolidated = invoice?.invoiceType === 50;

  return {
    isConsolidated,
    invoiceNo: invoice?.invoiceNo || invoice?.invoiceNumber || "",
    date: invoice?.invoiceDate || invoice?.createdAt || new Date().toISOString(),
    company: {
      name: attrs.stationName || "",
      phone: attrs.stationPhone || "",
      email: attrs.stationEmail || "",
      address: attrs.stationAddress || "",
      vat: attrs.stationTaxNumber || "",
    },
    customer: {
      name: attrs.customerName || "",
      phone: attrs.customerPhone || "",
      email: attrs.customerEmail || attrs.companyEmail || "",
      address: attrs.customerAddress || "",
      vat: attrs.customerTaxNumber || "",
    },
    vehicle: {
      make: attrs.makeTitle || attrs.make || "",
      model: attrs.modelTitle || attrs.model || "",
      fuelType: attrs.fuelTypeTitle || "",
      mileage: attrs.mileage != null ? String(attrs.mileage) : "",
      vin: attrs.vin || "",
      plateNumber: attrs.plateNumber || attrs.vehiclePlateNo || "",
      color: attrs.colorTitle || attrs.color || "",
      year: attrs.year != null ? String(attrs.year) : "",
    },
    items: collectLineItems(invoice, settings),
    deliveryNotes: collectDeliveryNotes(invoice),
    subtotal: Number(invoice.subTotal ?? invoice.subtotal) || 0,
    discount: Number(invoice.discount) || 0,
    tax: Number(invoice.taxAmount ?? invoice.tax) || 0,
    total: Number(invoice.totalAmount ?? invoice.total) || 0,
    paidAmount: Number(invoice.paidAmount) || 0,
    dueAmount: Number(invoice.dueAmount ?? invoice.balance) || 0,
    creditAmount: Number(invoice.creditAmount) || 0,
    paymentMode: invoice.paymentModeTitle || invoice.paymentMode || "",
    footerText: settings.footerText,
    termsText: invoice.terms || attrs.terms || "",
    notesText: invoice.notes || attrs.notes || "",
    logoUrl: attrs.stationLogo || attrs.stationLogoUrl || attrs.logo,
  };
}
